<template>
  <section class="px-4 py-6 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-7xl">
      <div class="rounded-[1.75rem] border border-slate-200 bg-white px-5 py-5 shadow-sm sm:px-6 sm:py-6">
        <div class="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
          <div class="flex min-w-0 items-start gap-3">
            <HistoryBackButton />
            <div class="min-w-0">
              <p class="m-0 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Boards</p>
              <h1 class="mt-2 text-2xl font-bold text-slate-950 sm:text-3xl">
                {{ activeBoard ? activeBoard.name : 'Saved Boards' }}
              </h1>
              <p class="mt-2 text-sm font-medium text-slate-500">
                {{ activeBoard ? 'Flyers saved in this board.' : 'Keep related flyers grouped together for quick access.' }}
              </p>
            </div>
          </div>
          <button
            v-if="activeBoard"
            type="button"
            class="inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            @click="closeBoard"
          >
            <i class="ri-grid-line" aria-hidden="true"></i>
            <span>All boards</span>
          </button>
        </div>

        <template v-if="!activeBoard">
          <div v-if="isLoading" class="py-12 text-center text-sm font-bold text-slate-500">
            Loading boards...
          </div>

          <div v-else-if="loadError" class="mt-5 rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
            {{ loadError }}
          </div>

          <div v-else-if="!boards.length" class="py-12 text-center">
            <p class="m-0 text-lg font-bold text-slate-900">No saved boards yet</p>
            <p class="mt-2 text-sm font-medium text-slate-500">Save flyers to boards and they will appear here.</p>
          </div>

          <div v-else class="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <button
              v-for="board in boards"
              :key="board.id"
              type="button"
              class="group min-h-[8.5rem] rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:border-blue-200 hover:bg-blue-50/30"
              @click="openBoard(board)"
            >
              <span class="flex items-center justify-between gap-3">
                <span class="board-card-icon grid h-9 w-9 place-items-center rounded-xl bg-blue-50 text-lg text-blue-700 ring-1 ring-blue-100 transition group-hover:bg-white">
                  <i class="ri-layout-masonry-line" aria-hidden="true"></i>
                </span>
                <span class="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500">
                  {{ board.count }} {{ board.count === 1 ? 'flyer' : 'flyers' }}
                </span>
              </span>
              <span class="mt-4 block truncate text-base font-semibold text-slate-950">{{ board.name }}</span>
              <span class="mt-1 block line-clamp-2 text-sm leading-5 text-slate-500">
                {{ board.description || 'Open this board to view saved flyers.' }}
              </span>
              <span class="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700">
                <span>Open board</span>
                <i class="ri-arrow-right-line" aria-hidden="true"></i>
              </span>
            </button>
          </div>
        </template>

        <template v-else>
          <div v-if="flyersLoading" class="py-12 text-center text-sm font-bold text-slate-500">
            Loading flyers...
          </div>
          <div v-else-if="flyersError" class="mt-5 rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
            {{ flyersError }}
          </div>
          <div v-else-if="!boardFlyers.length" class="py-12 text-center">
            <p class="m-0 text-lg font-bold text-slate-900">No flyers in this board</p>
            <p class="mt-2 text-sm font-medium text-slate-500">Saved flyers for this board will appear here.</p>
          </div>
          <div v-else class="mt-5">
            <div class="board-flyer-masonry">
              <div
                v-for="(column, columnIndex) in boardFlyerColumns"
                :key="`board-flyer-column-${columnIndex}`"
                class="board-flyer-masonry-column"
              >
                <FlyerCard v-for="flyer in column" :key="flyer.id" :flyer="flyer" view="masonry" />
              </div>
            </div>
            <div ref="loadMoreTarget" class="mt-5 grid min-h-20 place-items-center">
              <span v-if="flyersLoadingMore" class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-500 shadow-sm">
                Loading more flyers...
              </span>
              <span v-else-if="hasMoreFlyers" class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-500 shadow-sm">
                Scroll to load more
              </span>
              <span v-else class="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-500">
                You are all caught up.
              </span>
            </div>
          </div>
        </template>
      </div>
    </div>

  </section>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import HistoryBackButton from '@/components/common/HistoryBackButton.vue'
import FlyerCard from '@/components/common/FlyerCard.vue'
import api from '@/services/api'
import { formatFlyerListDate, parseApiDate } from '@/utils/date'
import { useSessionStore } from '@/stores/session'
import { getFlyerBoardId } from '@/utils/flyerDetail'

const session = useSessionStore()
const boards = ref([])
const isLoading = ref(false)
const loadError = ref('')
const activeBoard = ref(null)
const boardFlyers = ref([])
const flyersLoading = ref(false)
const flyersLoadingMore = ref(false)
const flyersError = ref('')
const currentFlyerPage = ref(0)
const hasMoreFlyers = ref(false)
const loadMoreTarget = ref(null)
const masonryColumnCount = ref(2)
let observer

const normalizeBoardName = (value) => String(value ?? '').trim()

const getBoardApiId = (item = {}) =>
  item.board_id ??
  item.boardid ??
  item.boardId ??
  item.saved_board_id ??
  item.savedBoardId ??
  item.saved_id ??
  item.savedId ??
  item.id ??
  item.value ??
  ''

const normalizeBoard = (item = {}, index = 0) => {
  const boardApiId = getBoardApiId(item)

  return {
    id: String(boardApiId || `board-${index + 1}`),
    apiId: String(boardApiId || ''),
    name: normalizeBoardName(item.name ?? item.board_name ?? item.boardName ?? item.title ?? item.label ?? 'Saved Board'),
    description: String(item.description ?? item.desc ?? item.subtitle ?? '').trim(),
    count: Number(item.count ?? item.flyer_count ?? item.flyers_count ?? item.total_flyers ?? item.total ?? 0),
    flyers: Array.isArray(item.flyers) ? item.flyers : Array.isArray(item.items) ? item.items : []
  }
}

const distributeByColumn = (items, columnCount) => {
  const count = Math.max(1, Number(columnCount) || 1)
  const columns = Array.from({ length: count }, () => [])
  items.forEach((item, index) => {
    columns[index % count].push(item)
  })
  return columns
}

const boardFlyerColumns = computed(() => distributeByColumn(boardFlyers.value, masonryColumnCount.value))

const updateMasonryColumnCount = () => {
  if (typeof window === 'undefined') return
  masonryColumnCount.value = window.innerWidth >= 1280 ? 4 : window.innerWidth >= 768 ? 3 : 2
}

const boardFromGroup = ([name, flyers], index = 0) => {
  const flyerList = Array.isArray(flyers) ? flyers : []
  const latestFlyer = flyerList[0] || {}
  const boardId = getBoardApiId(latestFlyer) || latestFlyer.board?.id || ''
  return {
    id: String(boardId || `board-${index}-${name}`),
    apiId: String(boardId || ''),
    name: normalizeBoardName(name),
    description: latestFlyer.description ? `Latest: ${String(latestFlyer.description).slice(0, 80)}` : '',
    count: flyerList.length,
    flyers: flyerList
  }
}

const uniqueBoardsByName = (rows = []) => {
  const boardMap = new Map()

  rows.forEach((board, index) => {
    if (!board?.name) return
    const key = board.name.toLowerCase()
    const existing = boardMap.get(key)
    if (existing) {
        boardMap.set(key, {
          ...existing,
          apiId: existing.apiId || board.apiId,
          count: Math.max(existing.count, board.count || 0),
          description: existing.description || board.description,
          flyers: existing.flyers?.length ? existing.flyers : board.flyers
        })
      return
    }

    boardMap.set(key, { ...board, id: board.id || `board-${index + 1}` })
  })

  return Array.from(boardMap.values())
}

const extractBoardRows = (payload) => {
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.list,
    payload.data,
    payload.result,
    payload.boards,
    payload.saved_boards,
    payload.data?.list,
    payload.data?.data,
    payload.data?.result,
    payload.data?.boards,
    payload.data?.saved_boards
  ]

  const keyedBoardGroups = candidates
    .filter((candidate) => candidate && typeof candidate === 'object' && !Array.isArray(candidate))
    .flatMap((candidate) =>
      Object.entries(candidate)
        .filter(([key, value]) => key !== 'boards' && Array.isArray(value))
        .map(boardFromGroup)
    )

  const explicitBoardRows = candidates
    .filter(Array.isArray)
    .flatMap((candidate) => candidate.map(normalizeBoard))

  return uniqueBoardsByName([...keyedBoardGroups, ...explicitBoardRows])
}

const loadBoards = async () => {
  const userId = session.userId || session.user?.user_id || ''
  if (!userId) {
    boards.value = []
    return
  }

  isLoading.value = true
  loadError.value = ''

  try {
    const { data } = await api.listSavedFlyerBoards({ user_id: userId })
    boards.value = extractBoardRows(data).filter((board) => board.name)
  } catch (error) {
    boards.value = []
    loadError.value = error?.response?.data?.message || 'Boards could not be loaded.'
  } finally {
    isLoading.value = false
  }
}

const normalizeStatus = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (['1', 'active', 'published', 'true'].includes(normalized)) return 'Active'
  return 'Inactive'
}

const normalizeFlyer = (item = {}, index = 0) => {
  const dateValue = item.posted_at || item.post_date || item.created_at || item.created_time || item.updated_at || item.created_date || item.date || ''
  const parsedDate = parseApiDate(dateValue)
  const fallbackImage = Array.isArray(item.images) ? item.images[0] : ''
  const rawCategory = item.category || item.content_type || item.category_id || ''
  const boardId = getFlyerBoardId(item) || activeBoard.value?.apiId || activeBoard.value?.id || ''
  const categoryName = item.category_name || item.catergory_name || item.cat_name || item.content_type_name || item.flyer_category || item.category_title || (rawCategory && Number.isNaN(Number(rawCategory)) ? rawCategory : '') || 'Flyer'
  const city = item.city || item.area || item.locality || ''
  const locationText = item.location || item.location_name || item.address || city || item.state || item.street || item.country_id || item.country || ''

  return {
    id: item.id || item.flyer_id || `board-flyer-${index}`,
    title: item.title || item.name || item.flyer_title || '',
    category: categoryName,
    category_name: categoryName,
    contentTypeId: item.content_type || item.content_type_id || item.category_id || '',
    description: item.description || item.desc || item.caption || '',
    image: item.image || item.flyer_image || item.image_url || fallbackImage || '',
    param_code: item.param_code || item.paramCode || item.code || item.color_code || item.colorCode || item.color || '',
    code: item.code || item.param_code || item.paramCode || item.color_code || item.colorCode || item.color || '',
    address: item.address || item.flyer_address || item.location_name || item.location || city || '',
    location: locationText,
    city,
    area: item.area || '',
    locality: item.locality || '',
    state: item.state || '',
    street: item.street || '',
    country_id: item.country_id || '',
    country: item.country || '',
    status: normalizeStatus(item.status ?? item.publish_status),
    boardId,
    board_id: boardId,
    userId: item.user_id || item.userid || item.uid || '',
    author: item.author || item.user_name || item.username || 'NetFlyr',
    authorAvatar: item.authorAvatar || item.profile_pic || item.user_image || item.user_img || '',
    likesCount: item.likesCount || item.likes_count || item.likes || 0,
    viewsCount: item.viewsCount || item.views_count || item.views || 0,
    hashTags: item.hashTags || item.hash_tags || '',
    date: formatFlyerListDate(parsedDate || dateValue) || 'No date',
    postedAt: item.posted_at || item.post_date || item.created_at || item.created_time || item.updated_at || item.created_date || item.date || dateValue,
    saved: true
  }
}

const flattenPayloadRows = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.list)) return payload.list
  if (payload?.list && typeof payload.list === 'object') {
    return Object.entries(payload.list).flatMap(([dateKey, items = []]) =>
      Array.isArray(items)
        ? items.map((item) => ({
            ...item,
            created_date: item.created_date || dateKey
          }))
        : []
    )
  }
  if (Array.isArray(payload?.saved_list)) return payload.saved_list
  return []
}

const mergeFlyers = (existing, incoming) => {
  const flyerMap = new Map(existing.map((flyer) => [String(flyer.id), flyer]))
  incoming.forEach((flyer) => flyerMap.set(String(flyer.id), flyer))
  return Array.from(flyerMap.values())
}

const toNumericBoardId = (value) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) && numeric > 0 ? numeric : ''
}

const observeLoadMoreTarget = async () => {
  await nextTick()
  if (!observer) return
  observer.disconnect()
  if (!loadMoreTarget.value || !hasMoreFlyers.value || !activeBoard.value) return
  observer.observe(loadMoreTarget.value)
}

const loadMoreBoardFlyers = () => {
  if (flyersLoading.value || flyersLoadingMore.value || !hasMoreFlyers.value) return
  loadBoardFlyers({ page: currentFlyerPage.value + 1, append: true })
}

const loadBoardFlyers = async ({ page = 1, append = false } = {}) => {
  const boardId = toNumericBoardId(activeBoard.value?.apiId || activeBoard.value?.id)
  const userId = session.userId || session.user?.user_id || ''
  if (!userId) return
  if (!boardId) {
    flyersError.value = 'Board flyers could not be loaded because the board id is missing.'
    boardFlyers.value = []
    hasMoreFlyers.value = false
    return
  }

  if (append) {
    flyersLoadingMore.value = true
  } else {
    flyersLoading.value = true
    flyersError.value = ''
    boardFlyers.value = Array.isArray(activeBoard.value?.flyers) ? activeBoard.value.flyers.map(normalizeFlyer) : []
    currentFlyerPage.value = 0
    hasMoreFlyers.value = false
  }

  try {
    const { data } = await api.savedFlyersUser({
      user_id: userId,
      page,
      board_id: boardId
    })
    const rows = flattenPayloadRows(data)
    const normalizedRows = rows.map((item, index) => normalizeFlyer(item, `${page}-${index}`))
    boardFlyers.value = append ? mergeFlyers(boardFlyers.value, normalizedRows) : (normalizedRows.length ? normalizedRows : boardFlyers.value)
    currentFlyerPage.value = normalizedRows.length ? page : Math.max(currentFlyerPage.value, page - 1)
    const totalPages = Number(data?.total_pages || data?.last_page || data?.pagination?.total_pages || data?.pagination?.last_page || 0)
    hasMoreFlyers.value = totalPages ? page < totalPages : normalizedRows.length > 0
  } catch (error) {
    if (!append) {
      boardFlyers.value = []
      flyersError.value = error?.response?.data?.message || 'Board flyers could not be loaded.'
    }
  } finally {
    flyersLoading.value = false
    flyersLoadingMore.value = false
    observeLoadMoreTarget()
  }
}

const openBoard = (board) => {
  activeBoard.value = board
  loadBoardFlyers({ page: 1, append: false })
}

const closeBoard = () => {
  activeBoard.value = null
  boardFlyers.value = []
  flyersError.value = ''
  currentFlyerPage.value = 0
  hasMoreFlyers.value = false
  observer?.disconnect()
}

onMounted(() => {
  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        loadMoreBoardFlyers()
      }
    },
    { rootMargin: '260px 0px' }
  )
  updateMasonryColumnCount()
  window.addEventListener('resize', updateMasonryColumnCount)
  loadBoards()
})

onUnmounted(() => {
  observer?.disconnect()
  window.removeEventListener('resize', updateMasonryColumnCount)
})
</script>

<style scoped>
.board-flyer-masonry {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  align-items: start;
}

.board-flyer-masonry-column {
  display: grid;
  min-width: 0;
  gap: 0.85rem;
  align-content: start;
}

@media (min-width: 768px) {
  .board-flyer-masonry {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .board-flyer-masonry {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
